// src/pages/DealDetail.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { dealAPI, userAPI, reviewAPI, chatAPI, metricsAPI } from "../services/api";
import { toast } from "react-toastify";
import useSeo from "../hooks/useSeo";
import { feeFromWeight } from "../utils/shipping";
import {
  FiEye,
  FiHeart,
  FiMapPin,
  FiClock,
  FiShoppingCart,
  FiStar,
  FiPhone,
  FiMail,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiMessageCircle,
  FiShoppingBag,
  FiExternalLink,
  FiCheck
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import Loading from "../components/common/Loading";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import config from "../config";
import { waNumber, waLink } from "../utils/whatsapp";
import { useSettings } from "../context/SettingsContext";
import { getAllDealImageUrls } from "../utils/dealImageHelper";
import { normalizeImagePath } from "../utils/imageUtils";
import PriceLadder from '../components/deals/PriceLadder';

const currencyFmt = (value) =>
  typeof value === "number"
    ? value.toLocaleString("fr-FR") + " MAD"
    : value
    ? `${value} MAD`
    : "0 MAD";

const safeGet = (v, fallback = null) => (v === undefined || v === null ? fallback : v);

// Libellés FR des caractéristiques (produits physiques)
const ATTR_LABELS = {
  size: 'Taille', shoe: 'Pointure', color: 'Couleur', volume: 'Volume',
  weight: 'Poids', capacity: 'Capacité', material: 'Matière', custom: 'Option'
};
const fmtAttrVal = (a, v) => (['volume', 'weight', 'capacity'].includes(a.type) ? `${v}${a.unit || ''}` : String(v));
const COLOR_HEX = {
  Noir: '#111827', Blanc: '#f3f4f6', Gris: '#9ca3af', Rouge: '#ef4444', Bleu: '#3b82f6',
  Vert: '#22c55e', Jaune: '#eab308', Rose: '#ec4899', Orange: '#f97316', Marron: '#92400e',
  Beige: '#e7d3b3', Violet: '#8b5cf6'
};

const DealDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const auth = useAuth();
  const user = auth?.user ?? null;

  const cart = useCart();
  const addToCart = cart?.addToCart ?? null;
  // LIEU CHOISI par le client quand l'annonce est disponible dans plusieurs
  // villes. Le coupon portera l'adresse et les horaires de ce lieu.
  const [chosenLocationId, setChosenLocationId] = useState('');

  const { settings: siteSettings } = useSettings() || {};

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  useSeo(deal ? {
    title: `${deal.title} | Maxula`,
    description: (deal.shortDescription || deal.description || '').toString().replace(/<[^>]+>/g, '').slice(0, 160),
    image: (Array.isArray(deal.imageUrls) && deal.imageUrls.find((u) => u && /^https?:/i.test(u)))
      || (Array.isArray(deal.images) && deal.images[0]) || undefined,
    type: 'product',
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: deal.title,
      description: (deal.shortDescription || deal.description || '').toString().replace(/<[^>]+>/g, '').slice(0, 300),
      image: Array.isArray(deal.imageUrls) ? deal.imageUrls.filter((u) => u && /^https?:/i.test(u)).slice(0, 5) : undefined,
      offers: {
        '@type': 'Offer',
        price: Number(deal.discountedPrice ?? deal.price ?? 0),
        priceCurrency: 'MAD',
        availability: (deal.unlimited || Number(deal.quantityRemaining) > 0) ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        url: typeof window !== 'undefined' ? window.location.href : undefined
      }
    }
  } : {});

  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const [optSel, setOptSel] = useState({}); // sélection par option (mode caractéristiques) : { position: valeurBrute }
  const [activeTab, setActiveTab] = useState('description');

  const [isFavorite, setIsFavorite] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);

  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Initialise la sélection par option (1re valeur de chaque caractéristique) au chargement du deal
  useEffect(() => {
    if (deal && deal.productType === 'physical' && Array.isArray(deal.attributes)) {
      const attrs = deal.attributes.filter((a) => a && a.type && Array.isArray(a.values) && a.values.length);
      if (attrs.length) {
        const init = {};
        attrs.forEach((a, p) => { init[p] = a.values[0]; });
        setOptSel(init);
      }
    }
  }, [deal]);

  // Bascule automatiquement l'image principale sur la photo associée à la couleur choisie
  useEffect(() => {
    if (!deal || deal.productType !== 'physical') return;
    // Parsing défensif : selon l'hébergement, ces champs peuvent arriver en CHAÎNE JSON
    // (MariaDB renvoie les colonnes JSON brutes). On parse ici pour que le changement
    // d'image fonctionne même si le backend déployé ne les a pas encore normalisés.
    const asArray = (v) => {
      if (Array.isArray(v)) return v;
      if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; } }
      return [];
    };
    const asObject = (v) => {
      if (v && typeof v === 'object' && !Array.isArray(v)) return v;
      if (typeof v === 'string') { try { const p = JSON.parse(v); return (p && typeof p === 'object' && !Array.isArray(p)) ? p : {}; } catch { return {}; } }
      return {};
    };
    const attrs = asArray(deal.attributes).filter((a) => a && a.type && Array.isArray(a.values) && a.values.length);
    const colorPos = attrs.findIndex((a) => a.type === 'color');
    if (colorPos < 0) return;
    const colorVal = optSel[colorPos] != null ? optSel[colorPos] : attrs[colorPos].values[0];
    const map = asObject(deal.colorImageMap);
    const idx = map[colorVal];
    // Nombre d'images réellement affichées (le carrousel utilise imageUrls)
    const displayLen = (Array.isArray(deal.imageUrls) && deal.imageUrls.length)
      ? deal.imageUrls.length
      : asArray(deal.images).length;
    const n = Number(idx);
    if (idx != null && idx !== '' && Number.isFinite(n) && n >= 0 && n < displayLen) setSelectedImage(n);
  }, [optSel, deal]);

  const loadDeal = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dealAPI.getOne(slug);
      const payload = res?.data?.data ?? res?.data ?? res ?? null;

      if (!mountedRef.current) return;

      if (!payload) {
        toast.error("Deal non trouvé");
        navigate("/deals");
        return;
      }

      const imageUrls = getAllDealImageUrls(payload, "/placeholder.png");

      const normalized = {
        ...payload,
        imageUrls,
        locations: Array.isArray(payload.locations)
          ? payload.locations
          : payload.location
          ? [payload.location]
          : payload.city
          ? [payload.city]
          : payload.cityRef?.name
          ? [payload.cityRef.name]
          : [],
        highlights: Array.isArray(payload.highlights)
          ? payload.highlights
          : payload.highlights
          ? [payload.highlights]
          : [],
        conditions: Array.isArray(payload.conditions)
          ? payload.conditions
          : payload.conditions
          ? [payload.conditions]
          : [],
        discountedPrice: Number(payload.discountedPrice ?? payload.price ?? payload.discounted_price ?? 0),
        originalPrice: Number(payload.originalPrice ?? payload.original_price ?? payload.priceBefore ?? 0),
        rating: Number(payload.rating ?? 0),
        reviewCount: Number(payload.reviewCount ?? payload.reviewsCount ?? payload.reviews ?? 0),
        quantityRemaining: Number(payload.quantityRemaining ?? payload.remaining ?? payload.stock ?? 0),
        unlimited: payload.unlimited === true || (Number(payload.quantityTotal) <= 0 && payload.quantityTotal != null),
        startDate: payload.startDate ?? payload.validFrom ?? payload.startDateISO,
        endDate: payload.endDate ?? payload.validUntil ?? payload.endDateISO,
        // Prix degressif.
        // ATTENTION : cette page RECONSTRUIT l'objet champ par champ. Tout
        // champ non repris ici est perdu, meme si l'API le renvoie — c'est
        // ce qui faisait disparaitre l'echelle de prix sans aucune erreur.
        quantityTiers: Array.isArray(payload.quantityTiers) ? payload.quantityTiers : [],
        priceLadder: Array.isArray(payload.priceLadder) ? payload.priceLadder : [],
        infoSections: Array.isArray(payload.infoSections) ? payload.infoSections : [],
        // Marketplace multi-types
        productType: payload.productType || 'service',
        variants: Array.isArray(payload.variants) ? payload.variants : [],
        weightGrams: payload.weightGrams ?? null,
        shippingFee: Number(payload.shippingFee) || 0,
        freeShippingThreshold: (payload.freeShippingThreshold != null && payload.freeShippingThreshold !== '') ? Number(payload.freeShippingThreshold) : null,
        shippingZones: Array.isArray(payload.shippingZones) ? payload.shippingZones : [],
        shippingMode: payload.shippingMode === 'weight' ? 'weight' : 'flat',
        shippingWeightTiers: Array.isArray(payload.shippingWeightTiers) ? payload.shippingWeightTiers : [],
        // ===== Fiche produit =====
        condition: payload.condition === 'used' || payload.condition === 'new' ? payload.condition : null,
        availability: ['in_stock', 'out_of_stock', 'incoming'].includes(payload.availability) ? payload.availability : 'in_stock',
        brand: payload.brand || null,
        brandLogo: payload.brandLogo || null,
        sku: payload.sku || null,
        barcode: payload.barcode || null,
        expiryDate: payload.expiryDate ? String(payload.expiryDate).slice(0, 10) : null,
        // Calcules par le serveur : on ne rejoue pas la regle cote client, sinon
        // les deux implementations finissent par diverger.
        isExpiredProduct: payload.isExpiredProduct === true,
        availabilityBlocked: payload.availabilityBlocked === true
      };

      setDeal(normalized);
      setSelectedImage(0);
      setQuantity(1);
      setSelectedVariantIdx(0);
      setActiveTab('description');
      setReviewRating(normalized.rating || 0);
      setReviewCount(normalized.reviewCount || 0);
    } catch (err) {
      console.error("loadDeal error", err);
      toast.error("Deal non trouvé");
      navigate("/deals");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [slug, navigate]);

  const incrementViews = useCallback(async () => {
    try {
      if (!slug || !dealAPI || typeof dealAPI.incrementViews !== "function") return;
      // UNE SEULE VUE PAR VISITE ET PAR ANNONCE.
      //
      // Sans ce garde-fou, chaque rafraichissement (F5), chaque retour arriere
      // et chaque remontage du composant ajoutait une vue. Le marchand aurait
      // vu un chiffre flatteur mais faux, et aurait pris des decisions dessus.
      // sessionStorage se vide a la fermeture de l'onglet : revenir demain
      // recompte, revenir dans la minute non.
      const cle = `deal_vu_${slug}`;
      try {
        if (window.sessionStorage && window.sessionStorage.getItem(cle)) return;
        if (window.sessionStorage) window.sessionStorage.setItem(cle, "1");
      } catch (e) {
        // Navigation privee ou stockage refuse : on compte quand meme plutot
        // que de ne rien compter du tout.
      }
      await dealAPI.incrementViews(slug);
    } catch (error) {
      console.error("Erreur incrémentation vues:", error);
    }
  }, [slug]);

  useEffect(() => {
    (async () => {
      await loadDeal();
      await incrementViews();
    })();
  }, [loadDeal, incrementViews]);

  useEffect(() => {
    let cancelled = false;

    const checkFavorite = async () => {
      if (!user || !deal) {
        setIsFavorite(false);
        return;
      }
      try {
        const res = await userAPI.getFavorites();
        const favs = res?.data?.data ?? res?.data ?? res ?? [];
        const arr = Array.isArray(favs) ? favs : favs.favorites ?? favs.items ?? [];
        if (cancelled) return;

        const dealId = deal._id || deal.id;
        const isFav = Array.isArray(arr) && arr.some((fav) => (fav?._id || fav?.id) === dealId);
        setIsFavorite(Boolean(isFav));
      } catch (error) {
        console.error("Erreur vérification favoris:", error);
      }
    };

    checkFavorite();
    return () => {
      cancelled = true;
    };
  }, [user, deal]);

  const loadReviews = useCallback(
    async (dealId) => {
      if (!dealId) return;
      setReviewsLoading(true);
      try {
        const res = await reviewAPI.getByDeal(dealId);
        const payload = res?.data?.data ?? res?.data ?? [];
        const arr = Array.isArray(payload) ? payload : [];
        setReviews(arr);

        const count = arr.length;
        if (count > 0) {
          const sum = arr.reduce((acc, r) => acc + (Number(r?.rating) || 0), 0);
          const avg = sum / count;
          setReviewRating(Number(avg.toFixed(1)));
          setReviewCount(count);
        } else {
          setReviewRating(deal?.rating || 0);
          setReviewCount(deal?.reviewCount || 0);
        }
      } catch (e) {
        console.error("loadReviews error", e);
        setReviews([]);
        setReviewRating(deal?.rating || 0);
        setReviewCount(deal?.reviewCount || 0);
      } finally {
        setReviewsLoading(false);
      }
    },
    [deal]
  );

  useEffect(() => {
    if (deal && (deal._id || deal.id)) loadReviews(deal._id || deal.id);
  }, [deal, loadReviews]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.info("Connectez-vous pour laisser un avis");
      navigate("/login");
      return;
    }
    if (!deal || (!deal._id && !deal.id)) {
      toast.error("Deal introuvable");
      return;
    }
    if (!myRating || myRating < 1 || myRating > 5) {
      toast.error("Choisissez une note entre 1 et 5");
      return;
    }
    if (!myComment.trim()) {
      toast.error("Le commentaire est obligatoire");
      return;
    }

    try {
      await reviewAPI.create({
        dealId: deal._id || deal.id,
        rating: myRating,
        comment: myComment
      });
      toast.success("Avis envoyé");
      setMyRating(0);
      setMyComment("");
      await loadReviews(deal._id || deal.id);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Erreur lors de l'envoi de votre avis";
      toast.error(msg);
    }
  };

  const renderStarsInput = () => (
    <div className="flex items-center space-x-1 mb-2">
      {[1, 2, 3, 4, 5].map((star) => (
        <button key={star} type="button" onClick={() => setMyRating(star)} className="focus:outline-none">
          <FiStar className={star <= myRating ? "text-yellow-400 fill-current" : "text-gray-300"} size={22} />
        </button>
      ))}
    </div>
  );

  const renderStarsReadOnly = (rating) => {
    const r = Number(rating) || 0;
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar key={star} className={star <= r ? "text-yellow-400 fill-current" : "text-gray-300"} size={14} />
        ))}
      </div>
    );
  };

  if (loading) return <Loading />;
  if (!deal) return null;

  // ===== Variantes / caractéristiques (produits physiques) =====
  const hasVariants = deal.productType === 'physical' && Array.isArray(deal.variants) && deal.variants.length > 0;
  // Caractéristiques exploitables comme sélecteurs par option (taille, couleur, etc.)
  const variantAttrs = (deal.productType === 'physical' && Array.isArray(deal.attributes))
    ? deal.attributes.filter((a) => a && a.type && Array.isArray(a.values) && a.values.length)
    : [];
  const usePerOption = hasVariants && variantAttrs.length > 0;

  // Reconstruit le label de variante depuis les options choisies (même format/ordre qu'à la génération)
  let resolvedIdx = selectedVariantIdx;
  if (usePerOption) {
    const lbl = variantAttrs.map((a, p) => fmtAttrVal(a, (optSel[p] != null ? optSel[p] : a.values[0]))).join(' / ');
    const mi = deal.variants.findIndex((v) => v.label === lbl);
    resolvedIdx = mi >= 0 ? mi : 0;
  }
  const selectedVariant = hasVariants ? (deal.variants[resolvedIdx] || deal.variants[0]) : null;
  // Une valeur d'option est disponible si au moins une variante la contenant a du stock
  const valueAvailable = (pos, rawVal) => {
    if (!usePerOption) return true;
    const f = fmtAttrVal(variantAttrs[pos], rawVal);
    return deal.variants.some((v) => {
      const parts = String(v.label || '').split(' / ');
      if (parts[pos] !== f) return false;
      return !(v.stock != null && v.stock !== '' && Number(v.stock) <= 0);
    });
  };
  // Étiquette de variante à partir d'une sélection (même format/ordre qu'à la génération)
  const labelFromSel = (sel) =>
    variantAttrs.map((a, p) => fmtAttrVal(a, sel[p] != null ? sel[p] : a.values[0])).join(' / ');
  const labelInStock = (label) => {
    const v = deal.variants.find((x) => x.label === label);
    if (!v) return false;
    return !(v.stock != null && v.stock !== '' && Number(v.stock) <= 0);
  };
  // Sélection d'une valeur d'axe avec AUTO-RÉPARATION : si la combinaison résultante
  // est en rupture, on ajuste les AUTRES axes vers la combinaison en stock la plus
  // proche contenant la valeur choisie — l'acheteur ne reste jamais bloqué sur une
  // combinaison morte alors qu'une combinaison valide existe avec son choix.
  const pickOption = (pos, rawVal) => {
    setOptSel((prev) => {
      const sel = { ...prev, [pos]: rawVal };
      if (usePerOption && !labelInStock(labelFromSel(sel))) {
        const f = fmtAttrVal(variantAttrs[pos], rawVal);
        const match = deal.variants.find((v) => {
          const parts = String(v.label || '').split(' / ');
          return parts[pos] === f && !(v.stock != null && v.stock !== '' && Number(v.stock) <= 0);
        });
        if (match) {
          const parts = String(match.label).split(' / ');
          variantAttrs.forEach((a, q) => {
            if (q === pos) return;
            const raw = a.values.find((val) => fmtAttrVal(a, val) === parts[q]);
            if (raw != null) sel[q] = raw;
          });
        }
      }
      return sel;
    });
    setQuantity(1);
  };
  const variantDelta = selectedVariant ? (Number(selectedVariant.priceDelta) || 0) : 0;  const basePrice = Number(deal.discountedPrice ?? deal.price ?? 0);
  const effectivePrice = Math.max(0, Math.round((basePrice + variantDelta) * 100) / 100);
  // Stock : si la variante porte un stock, il prime ; sinon stock global du deal
  const variantStock = (selectedVariant && selectedVariant.stock != null && selectedVariant.stock !== '')
    ? Number(selectedVariant.stock) : null;
  const effectiveStock = variantStock != null ? variantStock : (deal.quantityRemaining ?? 0);
  const soldOut = !deal.unlimited && effectiveStock <= 0;

  // ===== Fiche produit : disponibilite declaree + peremption =====
  // Le serveur refuse deja la commande (orderController) ; ici on empeche
  // simplement l'acheteur d'aller jusqu'au paiement pour rien.
  const availabilityLabels = {
    out_of_stock: 'Ce produit est actuellement en rupture de stock.',
    incoming: "Ce produit est en arrivage : il n'est pas encore commandable."
  };
  const availabilityNotice = deal.isExpiredProduct
    ? 'Ce produit a dépassé sa date de péremption et ne peut pas être commandé.'
    : (availabilityLabels[deal.availability] || null);
  const purchaseBlocked = soldOut || !!availabilityNotice;

  // ===== Caractéristiques (onglet specs) =====
  const specRows = [];
  if (Array.isArray(deal.attributes)) {
    deal.attributes.forEach((a) => {
      if (a && a.type && Array.isArray(a.values) && a.values.length) {
        specRows.push({ label: ATTR_LABELS[a.type] || 'Option', value: a.values.map((v) => fmtAttrVal(a, v)).join(', ') });
      }
    });
  }
  if (deal.brand) {
    // Le logo vient de la fiche marque : il suit ses mises a jour, il n'est pas
    // fige sur l'annonce.
    specRows.push({
      label: 'Marque',
      value: deal.brand,
      icon: deal.brandLogo ? normalizeImagePath(deal.brandLogo) : null
    });
  }
  if (deal.condition) {
    specRows.push({ label: 'État', value: deal.condition === 'used' ? 'Occasion' : 'Neuf' });
  }
  if (deal.expiryDate) {
    specRows.push({ label: 'À consommer avant le', value: new Date(deal.expiryDate).toLocaleDateString('fr-FR') });
  }
  if (deal.sku) specRows.push({ label: 'Référence', value: deal.sku });
  if (deal.barcode) specRows.push({ label: 'Code-barres', value: deal.barcode });
  if (deal.productType === 'physical' && deal.weightGrams) {
    const g = Number(deal.weightGrams) || 0;
    specRows.push({ label: 'Poids', value: g >= 1000 ? `${(g / 1000).toFixed(g % 1000 === 0 ? 0 : 2)} kg` : `${g} g` });
  }
  if (deal.productType === 'physical') {
    if (deal.shippingMode === 'weight') {
      // Estimation pour 1 unité (poids unitaire) ; le total dépend de la quantité.
      const est = feeFromWeight(deal.shippingWeightTiers, Number(deal.weightGrams) || 0, deal.shippingFee);
      specRows.push({ label: 'Livraison', value: est > 0 ? `dès ${currencyFmt(est)} (selon le poids)` : 'Gratuite' });
    } else {
      const sf = Number(deal.shippingFee) || 0;
      specRows.push({ label: 'Livraison', value: sf > 0 ? `${currencyFmt(sf)}` : 'Gratuite' });
    }
    if (deal.freeShippingThreshold != null && Number(deal.freeShippingThreshold) > 0) {
      specRows.push({ label: 'Livraison gratuite dès', value: currencyFmt(Number(deal.freeShippingThreshold)) });
    }
  }
  const hasSpecs = (Array.isArray(deal.highlights) && deal.highlights.length > 0)
    || specRows.length > 0
    || (Array.isArray(deal.conditions) && deal.conditions.length > 0);

  const discountPercentage = safeGet(
    deal.discountPercentage,
    deal.originalPrice > 0
      ? Math.round(((deal.originalPrice - deal.discountedPrice) / deal.originalPrice) * 100)
      : 0
  );

  const daysRemaining = (() => {
    const end = deal.endDate || deal.endDateISO || deal.validUntil || deal.end;
    if (!end) return 0;
    const diffMs = new Date(end).getTime() - Date.now();
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, Number.isFinite(days) ? days : 0);
  })();

  const isExpired = (() => {
    if (deal.status === 'expired') return true;
    const end = deal.endDate || deal.endDateISO || deal.validUntil || deal.end;
    return !!end && new Date(end).getTime() < Date.now();
  })();

  const images = Array.isArray(deal.imageUrls) && deal.imageUrls.length ? deal.imageUrls : ["/placeholder.png"];

  const locations =
    Array.isArray(deal.locations) && deal.locations.length ? deal.locations : deal.location ? [deal.location] : [];

  const toggleFavorite = async () => {
    if (!user) {
      toast.info("Connectez-vous pour gérer vos favoris");
      navigate("/login");
      return;
    }
    if (!deal || (!deal._id && !deal.id)) return;

    try {
      const dealId = deal._id || deal.id;

      if (typeof userAPI.toggleFavorite === "function") {
        await userAPI.toggleFavorite(dealId);
      } else if (isFavorite && typeof userAPI.removeFavorite === "function") {
        await userAPI.removeFavorite(dealId);
      } else if (!isFavorite && typeof userAPI.addFavorite === "function") {
        await userAPI.addFavorite(dealId);
      }

      const newValue = !isFavorite;
      setIsFavorite(newValue);
      toast.success(newValue ? "Ajouté aux favoris" : "Retiré de vos favoris");
    } catch (error) {
      console.error("toggleFavorite error", error);
      toast.error("Impossible de mettre à jour les favoris");
    }
  };

  const goPrevImage = () => setSelectedImage((c) => (c === 0 ? images.length - 1 : c - 1));
  const goNextImage = () => setSelectedImage((c) => (c === images.length - 1 ? 0 : c + 1));

  const openLightbox = (index) => {
    setSelectedImage(index);
    setIsLightboxOpen(true);
  };
  const closeLightbox = () => setIsLightboxOpen(false);

  const handleAddToCart = () => {
    if (!deal) return;

    if (!addToCart) {
      toast.error("Le panier est indisponible");
      return false;
    }

    if (!deal.unlimited && effectiveStock <= 0) {
      toast.error("Ce deal est épuisé");
      return false;
    }

    // Choix de ville OBLIGATOIRE des qu'il y en a plusieurs : laisser passer
    // reviendrait a imprimer une adresse au hasard sur le coupon.
    if (Array.isArray(deal.serviceLocations) && deal.serviceLocations.length > 1 && !chosenLocationId) {
      toast.error("Choisissez d'abord la ville où vous utiliserez votre coupon");
      return false;
    }

    const id = deal._id || deal.id || deal.slug;
    if (!id) {
      toast.error("Deal invalide");
      return false;
    }

    const item = {
      dealId: id,
      _id: id,
      title: selectedVariant ? `${deal.title} — ${selectedVariant.label}` : deal.title,
      price: effectivePrice,
      image: images[0] || "/placeholder.png",
      merchantId: (deal.merchantId && typeof deal.merchantId === 'object'
        ? (deal.merchantId.id || deal.merchantId._id)
        : deal.merchantId) || null,
      slug,
      productType: deal.productType || 'service',
      shippingFee: Number(deal.shippingFee) || 0,
      freeShippingThreshold: deal.freeShippingThreshold ?? null,
      shippingZones: Array.isArray(deal.shippingZones) ? deal.shippingZones : [],
      // Lieu retenu : le serveur le revalide contre l'annonce avant de figer
      // l'adresse sur le coupon.
      locationId: chosenLocationId || null,
      locationLabel: (deal.serviceLocations || []).find(
        (l) => String(l.locationId) === String(chosenLocationId))?.cityName || null,
      shippingMode: deal.shippingMode === 'weight' ? 'weight' : 'flat',
      weightGrams: Number(deal.weightGrams) || 0,
      shippingWeightTiers: Array.isArray(deal.shippingWeightTiers) ? deal.shippingWeightTiers : [],
      variantLabel: selectedVariant ? selectedVariant.label : null,
      variantDelta: selectedVariant ? variantDelta : 0,
      // Les paliers voyagent AVEC la ligne : le panier peut alors recalculer
      // le prix quand le client change la quantite, sans rappeler l'API.
      quantityTiers: Array.isArray(deal.quantityTiers) ? deal.quantityTiers : []
    };

    try {
      addToCart(item, quantity);
      toast.success("Ajouté au panier");
      return true;
    } catch (e) {
      console.error("handleAddToCart error", e);
      toast.error("Erreur lors de l'ajout au panier");
      return false;
    }
  };

  const handleBuyNow = () => {
    const ok = handleAddToCart();
    if (ok) navigate("/checkout");
  };

  const sup = siteSettings?.support || {};

  const companyName = config.SUPPORT_NAME || "—";
  const companyAddress = config.SUPPORT_ADDRESS || "";
  const companyPhone = sup.phone || config.SUPPORT_PHONE || "";
  const companyEmail = sup.email || config.SUPPORT_EMAIL || "";
  // Normalisé : les formats 00212... et 06... produisaient un lien wa.me invalide,
  // symptôme visible surtout sur iPhone (Safari suit l'URL au lieu de laisser
  // l'application WhatsApp rattraper le numéro comme le fait Android).
  const companyWhatsapp = waNumber(sup.whatsapp) || waNumber(config.SUPPORT_WHATSAPP_CLEAN) || "";

  const whatsappText = encodeURIComponent(`Bonjour, je suis intéressé par: ${deal.title}`);

  // Contact & paiement du MARCHAND (pas de la plateforme)
  const merchantObj = deal.merchantId && typeof deal.merchantId === 'object' ? deal.merchantId : null;
  const merchantWhatsapp = companyWhatsapp; // anti-desintermediation : JAMAIS le numero du marchand, toujours Maxula

  // Phase 0 (mesure) : trace un clic de contact hors-plateforme (fire-and-forget).
  const logLeak = (channel, context) => {
    try {
      metricsAPI.logLeak({
        channel, context,
        dealId: deal?.id || deal?._id || null,
        merchantId: (merchantObj?.id) || mId || null,
        source: 'deal_detail'
      }).catch(() => {});
    } catch (e) { /* noop */ }
  };

  // --- Identité du vendeur (carte vendeur) ---
  const mId = merchantObj?.id || (deal.merchantId && typeof deal.merchantId !== 'object' ? deal.merchantId : null);
  const mName = merchantObj?.businessName || '';
  const mSlug = merchantObj?.slug || '';
  // Lien boutique : on préfère le slug (stable), sinon le nom (résolu côté serveur
  // par correspondance souple), sinon l'id numérique (peut être périmé après migration).
  const mRef = mSlug || mName || mId;
  const mLogo = merchantObj?.logo ? normalizeImagePath(merchantObj.logo) : null;
  const mRating = Number(merchantObj?.rating || 0);
  const mReviews = Number(merchantObj?.totalReviews || 0);
  const mCity = (() => {
    let a = merchantObj?.addresses;
    if (typeof a === 'string') { try { a = JSON.parse(a); } catch (e) { a = []; } }
    if (!Array.isArray(a)) a = [];
    return a[0]?.city || '';
  })();
  const hasMerchant = !!(merchantObj && mName && mId);

  const askQuestion = async () => {
    if (!user) { navigate('/login'); return; }
    const dealId = deal?._id || deal?.id;
    if (!dealId) { toast.error('Annonce indisponible'); return; }
    try {
      const res = await chatAPI.start({ dealId });
      const conv = res.data?.data?.conversation;
      const convId = conv?._id || conv?.id;
      navigate(convId ? `/messages?c=${convId}` : '/messages');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Impossible de démarrer la discussion');
    }
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* IMAGE SECTION */}
          <div>
            <div className="relative rounded-xl overflow-hidden border bg-white">
              <img
                src={images[selectedImage]}
                alt={deal.title || "Deal"}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/placeholder.png";
                }}
                className="w-full h-[340px] md:h-[420px] object-contain cursor-zoom-in"
                onClick={() => openLightbox(selectedImage)}
              />

              {discountPercentage > 0 && (
                <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1.5 rounded-lg font-bold text-sm">
                  -{discountPercentage}%
                </div>
              )}

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={goPrevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white rounded-full p-2 shadow"
                    aria-label="Image précédente"
                  >
                    <FiChevronLeft size={22} />
                  </button>
                  <button
                    type="button"
                    onClick={goNextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white rounded-full p-2 shadow"
                    aria-label="Image suivante"
                  >
                    <FiChevronRight size={22} />
                  </button>
                </>
              )}
            </div>

            <div className="mt-3 flex gap-3">
              {images.slice(0, 4).map((img, index) => (
                <button
                  key={String(index)}
                  type="button"
                  onClick={() => setSelectedImage(index)}
                  className={`h-16 w-24 rounded-lg overflow-hidden border ${
                    selectedImage === index ? "ring-2 ring-primary-500 border-primary-500" : "border-gray-200"
                  }`}
                  aria-label={`Voir image ${index + 1}`}
                >
                  <img
                    src={img}
                    alt={`${deal.title || ""} ${index + 1}`}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/placeholder.png";
                    }}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* PRICE & ACTIONS SECTION */}
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight">{deal.title}</h1>

            <div className="mt-2 flex items-center gap-3">
              <div className="flex items-center gap-1 text-sm">
                <FiStar className="text-yellow-400" />
                <span className="font-semibold text-gray-900">{reviewRating ?? 0}</span>
                <span className="text-gray-500">({reviewCount ?? 0} avis)</span>
              </div>

              {/* NOMBRE DE VUES.
                  `deal.views` est deja renvoye par l'API : serializeDeal ne le
                  retire pas des sorties publiques, aucun appel supplementaire
                  n'est necessaire.
                  Affiche meme a zero, pour que l'information soit toujours
                  presente. Pour la masquer sur les annonces jamais consultees,
                  remplacez `>= 0` par `>= 1`. */}
              {Number(deal?.views ?? 0) >= 0 && (
                <div className="flex items-center gap-1 text-sm text-gray-500" title="Nombre de consultations de cette annonce">
                  <FiEye />
                  <span className="font-semibold text-gray-700">
                    {Number(deal.views) >= 1000
                      ? `${(Number(deal.views) / 1000).toFixed(1).replace('.0', '')}k`
                      : Number(deal.views)}
                  </span>
                  <span>{Number(deal?.views ?? 0) > 1 ? 'vues' : 'vue'}</span>
                </div>
              )}

              <button
                type="button"
                onClick={toggleFavorite}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-red-500"
                aria-pressed={isFavorite}
              >
                <FiHeart className={isFavorite ? "fill-current text-red-500" : ""} />
                <span>Favoris</span>
              </button>
            </div>

            {/* Vendu par : marchand (nom cliquable -> sa boutique). Aucune info sensible. */}
            {(mName || companyName) && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs font-medium text-gray-400">Vendu par</span>
                {hasMerchant ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/merchants/${encodeURIComponent(mRef)}`)}
                    title={`Voir la boutique de ${mName}`}
                    className="group inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white py-1 pl-1 pr-3 shadow-sm transition hover:border-primary-300 hover:bg-primary-50 hover:shadow"
                  >
                    {mLogo ? (
                      <img src={mLogo} alt={mName} className="h-7 w-7 rounded-full object-cover ring-1 ring-gray-200" />
                    ) : (
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-xs font-bold text-white">{(mName || "M").charAt(0).toUpperCase()}</span>
                    )}
                    <span className="text-sm font-semibold text-gray-900 group-hover:text-primary-700">{mName}</span>
                    {mCity && <span className="text-xs text-gray-400">· {mCity}</span>}
                    <FiExternalLink className="text-gray-300 transition group-hover:text-primary-600" size={14} />
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 py-1 pl-1 pr-3">
                    {(mName && mLogo) ? (
                      <img src={mLogo} alt={mName} className="h-7 w-7 rounded-full object-cover ring-1 ring-gray-200" />
                    ) : (
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-gray-400 to-gray-600 text-xs font-bold text-white">{(mName || companyName || "M").charAt(0).toUpperCase()}</span>
                    )}
                    <span className="text-sm font-semibold text-gray-900">{mName || companyName}</span>
                    {(mName && mCity) && <span className="text-xs text-gray-400">· {mCity}</span>}
                  </span>
                )}
              </div>
            )}
            <div className="mt-5 rounded-xl border bg-white p-5">
              <div className="flex items-end gap-3">
                <div className="text-3xl md:text-4xl font-extrabold text-primary-600">
                  {currencyFmt(effectivePrice)}
                </div>
                {(Number(deal.originalPrice) || 0) > basePrice && (
                  <div className="text-lg text-gray-500 line-through">{currencyFmt((Number(deal.originalPrice) || 0) + variantDelta)}</div>
                )}
              </div>

              {(Number(deal.originalPrice) || 0) > basePrice && (
                <div className="mt-1 text-sm font-semibold text-green-600">
                  Vous économisez{" "}
                  {currencyFmt((Number(deal.originalPrice) || 0) - basePrice)}
                </div>
              )}

              {/* Caractéristiques / variantes (produits physiques) */}
              {usePerOption ? (
                <div className="mt-5 space-y-4">
                  {variantAttrs.map((a, p) => {
                    const current = optSel[p] != null ? optSel[p] : a.values[0];
                    return (
                      <div key={p}>
                        <div className="text-sm font-medium text-gray-700 mb-2">
                          {ATTR_LABELS[a.type] || 'Option'}
                          <span className="text-gray-400 font-normal"> : {fmtAttrVal(a, current)}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {a.values.map((val, vi) => {
                            const sel = current === val;
                            const avail = valueAvailable(p, val);
                            if (a.type === 'color') {
                              const hex = COLOR_HEX[val] || '#ddd';
                              return (
                                <button
                                  key={vi}
                                  type="button"
                                  disabled={!avail}
                                  onClick={() => pickOption(p, val)}
                                  title={avail ? val : `${val} — indisponible`}
                                  aria-label={val}
                                  className={`relative w-9 h-9 rounded-full border-2 transition ${sel ? 'border-primary-600 ring-2 ring-primary-200' : 'border-gray-300'} ${avail ? 'hover:scale-105' : 'opacity-30 cursor-not-allowed'}`}
                                  style={{ backgroundColor: hex }}
                                >
                                  {sel && <FiCheck className="absolute inset-0 m-auto text-white drop-shadow" size={16} />}
                                </button>
                              );
                            }
                            return (
                              <button
                                key={vi}
                                type="button"
                                disabled={!avail}
                                onClick={() => pickOption(p, val)}
                                className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${sel ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} ${avail ? '' : 'opacity-40 cursor-not-allowed line-through'}`}
                              >
                                {fmtAttrVal(a, val)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {selectedVariant && (
                    <div className="text-xs text-gray-500">
                      {variantStock != null
                        ? (variantStock > 0 ? `${variantStock} en stock pour « ${selectedVariant.label} »` : 'Rupture pour cette combinaison')
                        : `Sélection : « ${selectedVariant.label} »`}
                      {variantDelta ? ` · ${variantDelta > 0 ? '+' : ''}${currencyFmt(variantDelta)}` : ''}
                    </div>
                  )}
                </div>
              ) : hasVariants ? (
                <div className="mt-5">
                  <div className="text-sm font-medium text-gray-700 mb-2">Caractéristiques</div>
                  <div className="flex flex-wrap gap-2">
                    {deal.variants.map((v, i) => {
                      const active = i === selectedVariantIdx;
                      const vDelta = Number(v.priceDelta) || 0;
                      const vOut = v.stock != null && v.stock !== '' && Number(v.stock) <= 0;
                      return (
                        <button
                          key={i}
                          type="button"
                          disabled={vOut}
                          onClick={() => { setSelectedVariantIdx(i); setQuantity(1); }}
                          className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${active ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} ${vOut ? 'opacity-40 cursor-not-allowed line-through' : ''}`}
                        >
                          {v.label}{vDelta ? ` (${vDelta > 0 ? '+' : ''}${currencyFmt(vDelta)})` : ''}
                        </button>
                      );
                    })}
                  </div>
                  {selectedVariant && variantStock != null && (
                    <div className="mt-2 text-xs text-gray-500">
                      {variantStock > 0 ? `${variantStock} en stock pour « ${selectedVariant.label} »` : 'Rupture pour cette option'}
                    </div>
                  )}
                </div>
              ) : null}

              {/* Prix dégressif : AVANT le sélecteur de quantité, et visible même
                  déconnecté. C'est un argument d'achat — le cacher derrière la
                  connexion reviendrait à ne le montrer qu'à ceux déjà convaincus. */}
              <PriceLadder
                ladder={deal.priceLadder}
                quantity={quantity}
                onSelect={(q) => setQuantity(
                  Math.min(deal.unlimited ? 9999 : (effectiveStock || q), q)
                )}
              />

              {/* ✅ QUANTITÉ - Visible seulement si connecté */}
              {user && (
                <div className="mt-5">
                  <div className="text-sm font-medium text-gray-700 mb-2">Quantité</div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50"
                      aria-label="Diminuer la quantité"
                    >
                      -
                    </button>
                    <div className="w-12 text-center text-lg font-semibold">{quantity}</div>
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.min(deal.unlimited ? 9999 : (effectiveStock || 1), q + 1))}
                      className="w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50"
                      aria-label="Augmenter la quantité"
                    >
                      +
                    </button>
                    <div className="text-sm text-gray-600">{deal.unlimited ? 'Stock illimité' : `${effectiveStock ?? 0} disponibles`}</div>
                  </div>
                </div>
              )}

              {/* BOUTONS D'ACTION */}
              <div className="mt-5 space-y-3">
                {/* Achat en ligne — CTA PRINCIPAL */}
                {isExpired ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center text-sm text-red-700 font-medium">
                    Cette offre est expirée et n'est plus disponible à l'achat.
                  </div>
                ) : availabilityNotice ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center text-sm text-amber-800 font-medium">
                    {availabilityNotice}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleBuyNow}
                    disabled={purchaseBlocked}
                    className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Acheter maintenant
                  </button>
                )}

                {/* Ajouter au panier */}
                {(deal.unlimited || effectiveStock > 0) && !isExpired && !availabilityNotice && (
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    className="w-full bg-[#e11d7b] hover:bg-[#c4156a] text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition"
                  >
                    <FiShoppingCart size={18} />
                    <span>Ajouter au panier</span>
                  </button>
                )}

                {/* Chat interne avec le marchand (on-platform) */}
                <button
                  type="button"
                  onClick={askQuestion}
                  className="w-full border-2 border-primary-600 text-primary-600 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-primary-50 transition"
                >
                  <FiMessageCircle size={18} />
                  <span>Poser une question au marchand</span>
                </button>

                {/* Option secondaire : WhatsApp Maxula (assistance / commande via le support). Jamais le numéro du marchand. */}
                {companyWhatsapp && !isExpired && (
                  <a
                    href={waLink(companyWhatsapp, `Bonjour, je suis intéressé par: ${deal.title}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => logLeak('whatsapp', 'deal_order')}
                    className="w-full text-sm text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition"
                  >
                    <FaWhatsapp size={16} />
                    <span>Commander via WhatsApp</span>
                  </a>
                )}
              </div>

              <div className="mt-5 border-t pt-4 space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <FiClock />
                  <span>
                    Se termine dans {daysRemaining} jour{daysRemaining > 1 ? "s" : ""}
                  </span>
                </div>
                {/* VILLES DISPONIBLES.
                    Une annonce multi-villes exige un choix AVANT l'achat : le
                    coupon porte l'adresse et les horaires du lieu retenu. Sans
                    ce choix, le client recevrait l'adresse de la ville
                    principale, souvent la mauvaise. */}
                {Array.isArray(deal.serviceLocations) && deal.serviceLocations.length > 1 ? (
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-1">
                      <FiMapPin />
                      <span className="font-semibold">Choisissez votre ville</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {deal.serviceLocations.map((l) => {
                        const on = String(chosenLocationId) === String(l.locationId);
                        return (
                          <button
                            key={l.locationId}
                            type="button"
                            onClick={() => setChosenLocationId(l.locationId)}
                            className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition ${
                              on
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
                            }`}
                            title={l.openingHours || ''}
                          >
                            {l.cityName}
                          </button>
                        );
                      })}
                    </div>
                    {chosenLocationId ? (
                      <p className="text-xs text-gray-500 mt-1">
                        {(deal.serviceLocations.find((l) => String(l.locationId) === String(chosenLocationId)) || {}).openingHours || ''}
                      </p>
                    ) : (
                      <p className="text-xs text-amber-700 mt-1">
                        Sélectionnez une ville : l'adresse de votre coupon en dépend.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FiMapPin />
                    <span className="truncate">
                      {(Array.isArray(deal.serviceLocations) && deal.serviceLocations[0]?.cityName)
                        || locations.join(", ") || "N/A"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* REST OF THE PAGE */}
        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* Onglets produit */}
            <div className="flex gap-1 border-b border-gray-200 mb-5 overflow-x-auto">
              {[
                { k: 'description', label: 'Description' },
                ...(hasSpecs ? [{ k: 'specs', label: 'Caractéristiques' }] : []),
                // Sections libres du marchand : un onglet chacune, dans l'ordre
                // qu'il a choisi. Une section vide n'arrive jamais ici (le
                // serveur les retire), donc aucun onglet ne peut etre vide.
                ...(deal.infoSections || []).map((sec, i) => ({ k: `info-${i}`, label: sec.title })),
                { k: 'reviews', label: `Avis${reviews.length ? ` (${reviews.length})` : ''}` },
              ].map((t) => (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setActiveTab(t.k)}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition ${activeTab === t.k ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Onglet : Description */}
            {activeTab === 'description' && (
              <div className="bg-white border rounded-xl p-5 text-gray-700 leading-relaxed whitespace-pre-line">
                {deal.description || "Aucune description fournie."}
              </div>
            )}

            {/* Onglets : sections libres du marchand */}
            {(deal.infoSections || []).map((sec, i) => (
              activeTab === `info-${i}` ? (
                <div
                  key={`info-${i}`}
                  className="bg-white border rounded-xl p-5 text-gray-700 leading-relaxed whitespace-pre-line"
                >
                  {sec.body}
                </div>
              ) : null
            ))}

            {/* Onglet : Caractéristiques */}
            {activeTab === 'specs' && (
              <div className="space-y-6">
                {Array.isArray(deal.highlights) && deal.highlights.length > 0 && (
                  <div className="bg-white border rounded-xl p-5">
                    <h3 className="text-lg font-bold mb-3">Points forts</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      {deal.highlights.map((h, i) => (<li key={i}>{h}</li>))}
                    </ul>
                  </div>
                )}

                {specRows.length > 0 && (
                  <div className="bg-white border rounded-xl p-5">
                    <h3 className="text-lg font-bold mb-3">Caractéristiques</h3>
                    <dl className="divide-y divide-gray-100">
                      {specRows.map((row, i) => (
                        <div key={i} className="flex justify-between gap-4 py-2 text-sm">
                          <dt className="text-gray-500">{row.label}</dt>
                          <dd className="text-gray-900 font-medium text-right flex items-center justify-end gap-2">
                            {row.icon && (
                              <img
                                src={row.icon}
                                alt=""
                                className="h-6 w-6 object-contain rounded bg-white"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            )}
                            <span>{row.value}</span>
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                {Array.isArray(deal.conditions) && deal.conditions.length > 0 && (
                  <div className="bg-white border rounded-xl p-5">
                    <h3 className="text-lg font-bold mb-3">Conditions</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      {deal.conditions.map((c, i) => (<li key={i}>{c}</li>))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Onglet : Avis */}
            {activeTab === 'reviews' && (
              <div>
                <div className="mb-6 bg-white border rounded-xl p-5">
                  <div className="font-semibold mb-2">Laisser un avis</div>
                  {user ? (
                    <form onSubmit={handleSubmitReview}>
                      {renderStarsInput()}
                      <textarea
                        className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        rows={3}
                        placeholder="Partagez votre expérience..."
                        value={myComment}
                        onChange={(e) => setMyComment(e.target.value)}
                      />
                      <button
                        type="submit"
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-semibold"
                      >
                        Envoyer mon avis
                      </button>
                    </form>
                  ) : (
                    <p className="text-sm text-gray-600">
                      <button
                        type="button"
                        onClick={() => navigate("/login")}
                        className="text-primary-600 underline font-semibold"
                      >
                        Connectez-vous
                      </button>{" "}
                      pour laisser un avis.
                    </p>
                  )}
                </div>

                {reviewsLoading ? (
                  <div className="text-gray-600">Chargement des avis...</div>
                ) : reviews.length > 0 ? (
                  <div className="space-y-4">
                    {reviews.map((r) => (
                      <div key={r._id} className="bg-white border rounded-xl p-5">
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-gray-900">
                            {r.userId?.firstName} {r.userId?.lastName}
                          </div>
                          {renderStarsReadOnly(r.rating)}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {new Date(r.createdAt).toLocaleDateString("fr-FR", {
                            year: "numeric",
                            month: "long",
                            day: "numeric"
                          })}
                        </div>
                        <div className="mt-2 text-gray-700">{r.comment}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500">Aucun avis pour le moment.</div>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-xl border bg-white p-4">
                {hasMerchant ? (
                  <>
                    <div className="text-sm font-semibold text-gray-900">Vendeur</div>
                    <button onClick={() => navigate(`/merchants/${encodeURIComponent(mRef)}`)} className="mt-3 flex items-start gap-3 w-full text-left group">
                      {mLogo ? (
                        <img src={mLogo} alt={mName} className="w-11 h-11 rounded-full object-cover border border-gray-200 shrink-0" />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center font-bold shrink-0">{(mName || "M").charAt(0).toUpperCase()}</div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-gray-900 truncate text-sm group-hover:text-primary-600">{mName}</div>
                        {mCity && (<div className="mt-0.5 text-xs text-gray-500 flex items-center gap-1"><FiMapPin size={11} /> {mCity}</div>)}
                        {mReviews > 0 && (<div className="mt-0.5 text-xs text-gray-500 flex items-center gap-1"><FiStar className="text-yellow-400 fill-current" size={11} /> {mRating.toFixed(1)} · {mReviews} avis</div>)}
                      </div>
                      <FiExternalLink className="text-gray-300 group-hover:text-primary-600 shrink-0" size={16} />
                    </button>
                    <div className="mt-4 space-y-2">
                      <button onClick={() => navigate(`/merchants/${encodeURIComponent(mRef)}`)} className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 text-white font-semibold py-2 text-sm hover:bg-primary-700">
                        <FiShoppingBag size={15} /> Voir la boutique
                      </button>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 text-[11px] text-gray-400 flex items-center gap-1">
                      <FiMessageCircle size={12} /> Besoin d'aide ? <a href="/support" className="text-primary-600 hover:underline font-medium">Support Maxula</a>
                    </div>
                  </>
                ) : (
                <>
                <div className="text-sm font-semibold text-gray-900">À propos de la société</div>

                <div className="mt-3 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-700 text-sm">
                    {(companyName || "M").charAt(0).toUpperCase()}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-gray-900 truncate text-sm">{companyName}</div>

                    {companyAddress && (
                      <div className="mt-1 text-xs text-gray-600">
                        <FiMapPin className="inline -mt-0.5 mr-1" />
                        {companyAddress}
                      </div>
                    )}

                    {companyPhone && (
                      <div className="mt-1 text-xs text-gray-600">
                        <FiPhone className="inline -mt-0.5 mr-1" />
                        {companyPhone}
                      </div>
                    )}

                    {companyEmail && (
                      <div className="mt-1 text-xs text-gray-600">
                        <FiMail className="inline -mt-0.5 mr-1" />
                        {companyEmail}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {companyPhone ? (
                    <a
                      href={`tel:${companyPhone}`}
                      className="w-full inline-flex items-center justify-center rounded-lg bg-primary-600 text-white font-semibold py-2 text-sm hover:bg-primary-700"
                    >
                      Contacter
                    </a>
                  ) : companyEmail ? (
                    <a
                      href={`mailto:${companyEmail}`}
                      className="w-full inline-flex items-center justify-center rounded-lg bg-primary-600 text-white font-semibold py-2 text-sm hover:bg-primary-700"
                    >
                      Contacter
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toast.info("Contact indisponible")}
                      className="w-full inline-flex items-center justify-center rounded-lg bg-primary-600 text-white font-semibold py-2 text-sm hover:bg-primary-700"
                    >
                      Contacter
                    </button>
                  )}

                  {merchantWhatsapp && (
                    <a
                      href={waLink(merchantWhatsapp)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center rounded-lg bg-green-500 text-white font-semibold py-2 text-sm hover:bg-green-600"
                    >
                      <FaWhatsapp className="mr-2" />
                      WhatsApp
                    </a>
                  )}
                </div>
                </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LIGHTBOX */}
      {isLightboxOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={closeLightbox}>
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white bg-black/60 rounded-full p-2 hover:bg-black"
            aria-label="Fermer l'image"
          >
            <FiX size={20} />
          </button>

          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goPrevImage();
                }}
                className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-3 shadow-lg"
                aria-label="Image précédente"
              >
                <FiChevronLeft size={26} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goNextImage();
                }}
                className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-3 shadow-lg"
                aria-label="Image suivante"
              >
                <FiChevronRight size={26} />
              </button>
            </>
          )}

          <img
            src={images[selectedImage]}
            alt={deal.title || "Deal"}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
          />
        </div>
      )}
    </>
  );
};

export default DealDetail;
